import React, { useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { Button, Modal, RobbieMark } from '../ui';
import { RFQAccessList, OrgMemberOption, RFQRole } from '../../types';
import { useRfqSharing } from '../../hooks/useRfqSharing';
import AccessRow from './AccessRow';
import MemberSearch from './MemberSearch';

/**
 * Sharing an RFQ: who has access now, and one search field to give it to
 * colleagues. Only the owner ever opens this — an editor has no Share button,
 * so there is no disabled control here explaining what they cannot do.
 *
 * Presentational; `useRfqSharing` owns the loading, the staged picks, the
 * grant and the refetch. Everything a refusal says arrives from the server
 * already written in Robbie's voice and is rendered as-is.
 */
export interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  rfqId: string | undefined;
  /** Named in the heading so the owner knows which RFQ they're sharing. */
  rfqName?: string;
}

/** What the modal renders once the data is in — split out so the gallery can drive it directly. */
export interface ShareModalViewProps {
  access: RFQAccessList | null;
  members: OrgMemberOption[];
  matches: OrgMemberOption[];
  staged: OrgMemberOption[];
  onToggleStaged: (userId: string) => void;
  onUnstage: (userId: string) => void;
  onUnstageLast: () => void;
  query: string;
  onQueryChange: (query: string) => void;
  role: RFQRole;
  onRoleChange: (role: RFQRole) => void;
  onShare: () => void;
  onRemove: (userId: string) => void;
  loading: boolean;
  sharing: boolean;
  removingUserId: string | null;
  error: string | null;
  searchRef?: React.RefObject<HTMLInputElement | null>;
}

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="m-0 mb-s3 text-table-header uppercase text-ink-muted">{children}</h3>
);

export const ShareModalView: React.FC<ShareModalViewProps> = ({
  access, members, matches, staged, onToggleStaged, onUnstage, onUnstageLast,
  query, onQueryChange, role, onRoleChange, onShare, onRemove,
  loading, sharing, removingUserId, error, searchRef,
}) => (
  <div className="flex flex-col gap-s6">
    {error && (
      <div role="alert" className="flex items-start gap-s4 rounded-card border border-danger-border bg-danger-bg px-s4 py-s3">
        <RobbieMark size="xs" aria-label="Robbie" />
        <p className="m-0 text-body-sm text-danger-ink">{error}</p>
      </div>
    )}

    <section>
      <SectionLabel>Add someone</SectionLabel>
      <MemberSearch
        members={members}
        matches={matches}
        staged={staged}
        onToggleStaged={onToggleStaged}
        onUnstage={onUnstage}
        onUnstageLast={onUnstageLast}
        query={query}
        onQueryChange={onQueryChange}
        role={role}
        onRoleChange={onRoleChange}
        onShare={onShare}
        sharing={sharing}
        disabled={loading}
        inputRef={searchRef}
      />
    </section>

    <section>
      <SectionLabel>Who has access</SectionLabel>
      {loading && !access ? (
        <div className="flex items-center gap-s3 px-s4 py-s5 text-body-sm text-ink-secondary">
          <Loader2 className="h-[13px] w-[13px] animate-spin" />
          Loading who has access…
        </div>
      ) : access ? (
        <div className="flex flex-col gap-s2">
          <AccessRow person={access.owner} />
          {access.collaborators.map((person) => (
            <AccessRow
              key={person.userId}
              person={person}
              onRemove={onRemove}
              busy={removingUserId === person.userId}
            />
          ))}
          {access.collaborators.length === 0 && (
            <p className="m-0 px-s4 py-s3 text-body-sm text-ink-secondary">
              Only you have access so far. Anyone you add here can change everything on this RFQ — sending
              outreach and accepting an award included — but only you can change this list.
            </p>
          )}
        </div>
      ) : null}
    </section>
  </div>
);

export const ShareModal: React.FC<ShareModalProps> = ({ open, onClose, rfqId, rfqName }) => {
  const searchRef = useRef<HTMLInputElement>(null);
  const sharing = useRfqSharing(rfqId, open);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      initialFocusRef={searchRef}
      title={rfqName ? `Share “${rfqName}”` : 'Share this RFQ'}
      footer={<Button variant="secondary" onClick={onClose}>Done</Button>}
    >
      <ShareModalView
        access={sharing.access}
        members={sharing.members}
        matches={sharing.matches}
        staged={sharing.staged}
        onToggleStaged={sharing.toggleStaged}
        onUnstage={sharing.unstage}
        onUnstageLast={sharing.unstageLast}
        query={sharing.query}
        onQueryChange={sharing.setQuery}
        role={sharing.role}
        onRoleChange={sharing.setRole}
        onShare={sharing.shareStaged}
        onRemove={sharing.removeCollaborator}
        loading={sharing.loading}
        sharing={sharing.sharing}
        removingUserId={sharing.removingUserId}
        error={sharing.error}
        searchRef={searchRef}
      />
    </Modal>
  );
};

export default ShareModal;
